import { useState } from "react";
import type {
  ContactImportRow,
  ExternalContactRow,
  GeneralFallbackConfig,
  OrgUserOption,
  UserDirectoryRow,
} from "@/lib/contactsDirectoryClient";
import type { DirectoryListingFilter, ImportMatch } from "./adminContactsDirectoryShared";
import type { OrgRole } from "./adminTypes";
import { createEmptyExternalForm } from "./adminContactsDirectoryShared";

export function useAdminContactsDirectoryState() {
  const [external, setExternal] = useState<ExternalContactRow[]>([]);
  const [userDir, setUserDir] = useState<UserDirectoryRow[]>([]);
  const [orgUsers, setOrgUsers] = useState<OrgUserOption[]>([]);
  const [editingUserRow, setEditingUserRow] = useState<UserDirectoryRow | null>(null);
  const [locations, setLocations] = useState<{ id: string; name: string }[]>([]);
  const [extForm, setExtForm] = useState(() => createEmptyExternalForm());
  const [inviteTarget, setInviteTarget] = useState<ExternalContactRow | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<OrgRole>("sales");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [migrateTarget, setMigrateTarget] = useState<ExternalContactRow | null>(null);
  const [migrateEmail, setMigrateEmail] = useState("");
  const [migrateProfileId, setMigrateProfileId] = useState("");
  const [showVisibilityHelp, setShowVisibilityHelp] = useState(false);
  const [showRouteHelp, setShowRouteHelp] = useState(false);
  const [showTopicsHelp, setShowTopicsHelp] = useState(false);
  const [roleGuidance, setRoleGuidance] = useState("");
  const [pathSource, setPathSource] = useState("");
  const [pathTarget, setPathTarget] = useState("");
  const [topicSource, setTopicSource] = useState("");
  const [topicTarget, setTopicTarget] = useState("");
  const [directoryMode, setDirectoryMode] = useState<"contacts" | "general_support" | "add_contact" | "tags" | "org_tree">("contacts");
  const [generalFallback, setGeneralFallback] = useState<GeneralFallbackConfig>({
    columns_available: false,
    source: null,
    source_id: null,
    is_valid: false,
    warning: "General support fallback settings are not available yet.",
  });
  const [generalFallbackChoice, setGeneralFallbackChoice] = useState("");
  const [generalFallbackBusy, setGeneralFallbackBusy] = useState(false);
  const [hasAutoOpenedGeneralFallback, setHasAutoOpenedGeneralFallback] = useState(false);
  const [pendingImportRows, setPendingImportRows] = useState<ContactImportRow[]>([]);
  const [importMatches, setImportMatches] = useState<ImportMatch[]>([]);
  const [applyingImport, setApplyingImport] = useState(false);
  const [directoryListingFilter, setDirectoryListingFilter] = useState<DirectoryListingFilter>("all");
  const [showDeactivatedExternal, setShowDeactivatedExternal] = useState(false);
  const [externalSearch, setExternalSearch] = useState("");
  const [externalVisibleCount, setExternalVisibleCount] = useState<5 | 10 | 20>(10);
  const [externalPage, setExternalPage] = useState(1);
  const [openExternalActionMenuId, setOpenExternalActionMenuId] = useState<string | null>(null);
  const [showSubordinatePicker, setShowSubordinatePicker] = useState(false);
  const [subordinateManagerKey, setSubordinateManagerKey] = useState("");
  const [selectedSubordinateKeys, setSelectedSubordinateKeys] = useState<string[]>([]);
  const [hideAlreadyAssignedSubordinates, setHideAlreadyAssignedSubordinates] = useState(false);
  const [assigningSubordinates, setAssigningSubordinates] = useState(false);
  const [lastSubordinateAssignment, setLastSubordinateAssignment] = useState<{
    managerLabel: string;
    subordinateLabels: string[];
    assignedAt: number;
  } | null>(null);
  const [showReportingInfo, setShowReportingInfo] = useState(false);
  const [reportingTreeViewMode, setReportingTreeViewMode] = useState<"org_chart" | "stacked">("org_chart");
  const [showReportingTreeDebugJson, setShowReportingTreeDebugJson] = useState(false);
  const [collapsedReportingKeys, setCollapsedReportingKeys] = useState<string[]>([]);
  const [contactsDisplayMode, setContactsDisplayMode] = useState<"list" | "full">("list");
  const [expandedContactKeys, setExpandedContactKeys] = useState<string[]>([]);
  const [showImportFieldsHelp, setShowImportFieldsHelp] = useState(false);
  const [showQuickRoleGuidance, setShowQuickRoleGuidance] = useState(false);
  const [showOrgChartFields, setShowOrgChartFields] = useState(false);
  const [showDownstreamCap, setShowDownstreamCap] = useState(false);
  const [showAllTopicOptions, setShowAllTopicOptions] = useState(false);
  const [locationsOrgColumn, setLocationsOrgColumn] = useState<"org_id" | "organization_id" | null>(null);
  const [userFormBusy, setUserFormBusy] = useState(false);

  return {
    external, setExternal, userDir, setUserDir, orgUsers, setOrgUsers, editingUserRow, setEditingUserRow, locations, setLocations,
    extForm, setExtForm, inviteTarget, setInviteTarget, inviteEmail, setInviteEmail, inviteRole, setInviteRole, inviteBusy, setInviteBusy,
    migrateTarget, setMigrateTarget, migrateEmail, setMigrateEmail, migrateProfileId, setMigrateProfileId, showVisibilityHelp, setShowVisibilityHelp,
    showRouteHelp, setShowRouteHelp, showTopicsHelp, setShowTopicsHelp, roleGuidance, setRoleGuidance, pathSource, setPathSource, pathTarget, setPathTarget,
    topicSource, setTopicSource, topicTarget, setTopicTarget, directoryMode, setDirectoryMode, generalFallback, setGeneralFallback,
    generalFallbackChoice, setGeneralFallbackChoice, generalFallbackBusy, setGeneralFallbackBusy, hasAutoOpenedGeneralFallback, setHasAutoOpenedGeneralFallback,
    pendingImportRows, setPendingImportRows, importMatches, setImportMatches, applyingImport, setApplyingImport, directoryListingFilter, setDirectoryListingFilter,
    showDeactivatedExternal, setShowDeactivatedExternal, externalSearch, setExternalSearch, externalVisibleCount, setExternalVisibleCount,
    externalPage, setExternalPage, openExternalActionMenuId, setOpenExternalActionMenuId, showSubordinatePicker, setShowSubordinatePicker,
    subordinateManagerKey, setSubordinateManagerKey, selectedSubordinateKeys, setSelectedSubordinateKeys, hideAlreadyAssignedSubordinates,
    setHideAlreadyAssignedSubordinates, assigningSubordinates, setAssigningSubordinates, lastSubordinateAssignment, setLastSubordinateAssignment,
    showReportingInfo, setShowReportingInfo, reportingTreeViewMode, setReportingTreeViewMode, showReportingTreeDebugJson, setShowReportingTreeDebugJson,
    collapsedReportingKeys, setCollapsedReportingKeys, contactsDisplayMode, setContactsDisplayMode, expandedContactKeys, setExpandedContactKeys,
    showImportFieldsHelp, setShowImportFieldsHelp, showQuickRoleGuidance, setShowQuickRoleGuidance, showOrgChartFields, setShowOrgChartFields,
    showDownstreamCap, setShowDownstreamCap, showAllTopicOptions, setShowAllTopicOptions, locationsOrgColumn, setLocationsOrgColumn, userFormBusy, setUserFormBusy,
  };
}
